/*
 * Lets users write Box pathname exclusions as simple globs while Bender keeps storing and
 * evaluating regular expressions. The frontend owns the translation in both directions so the
 * backend contract stays a list of regex sources.
 */

const REGEX_METACHARACTERS = new Set(['\\', '^', '$', '.', '|', '?', '*', '+', '(', ')', '[', ']', '{', '}']);
const GLOB_ANY_TEXT = '*';
const GLOB_ONE_CHARACTER = '?';
const REGEX_ANY_TEXT = '.*';
const REGEX_ONE_CHARACTER = '.';
const REGEX_START = '^';
const REGEX_END = '$';

const toRegExpToken = (character: string): string => {
  if (character === GLOB_ANY_TEXT) return REGEX_ANY_TEXT;
  if (character === GLOB_ONE_CHARACTER) return REGEX_ONE_CHARACTER;
  return REGEX_METACHARACTERS.has(character) ? `\\${character}` : character;
};

/** Anchors the result so a glob matches a whole file name or folder path, never a fragment. */
export const globToRegExpSource = (glob: string): string =>
  `${REGEX_START}${Array.from(glob, toRegExpToken).join('')}${REGEX_END}`;

const parseGlobBody = (body: string): string | null => {
  const glob: string[] = [];
  let index = 0;
  while (index < body.length) {
    const character = body[index];
    if (body.startsWith(REGEX_ANY_TEXT, index)) {
      glob.push(GLOB_ANY_TEXT);
      index += REGEX_ANY_TEXT.length;
    } else if (character === REGEX_ONE_CHARACTER) {
      glob.push(GLOB_ONE_CHARACTER);
      index += 1;
    } else if (character === '\\') {
      glob.push(body[index + 1] ?? '');
      index += 2;
    } else if (REGEX_METACHARACTERS.has(character)) {
      return null;
    } else {
      glob.push(character);
      index += 1;
    }
  }
  return glob.join('');
};

/**
 * Recovers the glob behind a regex source that `globToRegExpSource` produced, or returns
 * `null` for any other source (for example a hand-written legacy regex).
 */
export const regExpSourceToGlob = (source: string): string | null => {
  if (source.length <= REGEX_START.length + REGEX_END.length ||
    !source.startsWith(REGEX_START) ||
    !source.endsWith(REGEX_END)) {
    return null;
  }
  const glob = parseGlobBody(source.slice(REGEX_START.length, -REGEX_END.length));
  // Re-encoding rejects escapes the converter never emits, such as `\*` or `\d`.
  return glob !== null && globToRegExpSource(glob) === source ? glob : null;
};
